import { useState, useEffect, useCallback, useMemo } from 'react';
import { propertiesApi } from '../services/propertyApi';
import { PropertyWithDetails, PropertySearchParams, CreatePropertyRequest, UpdatePropertyRequest } from '../types';
import { PaginatedResponse } from '../../../../types';
import { debounce } from '../../../../utils/validation';
import { useLoadingState } from '../../../../hooks/useLoadingState';

export const useProperties = () => {
  const [properties, setProperties] = useState<PropertyWithDetails[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { isLoading: globalLoading, message: loadingMessage, startLoading, stopLoading } = useLoadingState(250);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0
  });

  const [searchParams, setSearchParams] = useState<PropertySearchParams>({
    search: '',
    page: 1,
    limit: 10,
    sorts: [{
      field: 'name',
      direction: 'asc'
    }]
  });

  const fetchProperties = useCallback(async (params: PropertySearchParams = {}) => {
    setLoading(true);
    setError(null);
    startLoading('fetch-properties', 'Loading properties...');
    
    try {
      const response: PaginatedResponse<PropertyWithDetails> = await propertiesApi.getAll(params);
      setProperties(response.data);
      setPagination({
        page: response.page,
        limit: response.limit,
        total: response.total,
        totalPages: response.totalPages
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch properties');
    } finally {
      setLoading(false);
      stopLoading('fetch-properties');
    }
  }, [startLoading, stopLoading]);

  const debouncedFetchProperties = useMemo(() => debounce(fetchProperties, 300), [fetchProperties]);

  useEffect(() => {
    fetchProperties(searchParams);
  }, []);

  const updateSearchParams = useCallback((newParams: Partial<PropertySearchParams>) => {
    setSearchParams(prevParams => {
      const updatedParams = { ...prevParams, ...newParams };
      
      if (newParams.search !== undefined || newParams.fields !== undefined) {
        // Reset to first page when searching
        updatedParams.page = 1;
        debouncedFetchProperties(updatedParams);
      } else if (newParams.sorts !== undefined) {
        // Reset to first page when sorting
        updatedParams.page = 1;
        fetchProperties(updatedParams);
      } else if (newParams.page !== undefined || newParams.limit !== undefined) {
        // Handle page and limit changes immediately
        fetchProperties(updatedParams);
      } else {
        fetchProperties(updatedParams);
      }
      
      return updatedParams;
    });
  }, [debouncedFetchProperties, fetchProperties]);

  const createProperty = async (propertyData: CreatePropertyRequest) => {
    startLoading('create-property', 'Creating property...');
    try {
      setLoading(true);
      setError(null);
      const newProperty = await propertiesApi.create(propertyData);
      await fetchProperties(); // Refresh the list
      return newProperty;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create property');
      throw err;
    } finally {
      setLoading(false);
      stopLoading('create-property');
    }
  };

  const updateProperty = async (id: number, propertyData: UpdatePropertyRequest) => {
    startLoading('update-property', 'Updating property...');
    try {
      setLoading(true);
      setError(null);
      const updatedProperty = await propertiesApi.update(id, propertyData);
      await fetchProperties(); // Refresh the list
      return updatedProperty;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update property');
      throw err;
    } finally {
      setLoading(false);
      stopLoading('update-property');
    }
  };

  const deleteProperty = async (id: number) => {
    startLoading('delete-property', 'Deleting property...');
    try {
      await propertiesApi.delete(id);
      await fetchProperties(); // Refresh the list
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete property');
      return false;
    } finally {
      stopLoading('delete-property');
    }
  };

  const addContactToProperty = async (propertyId: number, contactId: number) => {
    startLoading('add-contact-to-property', 'Adding contact to property...');
    try {
      await propertiesApi.addContact(propertyId, contactId);
      await fetchProperties(); // Refresh the list
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add contact to property');
      return false;
    } finally {
      stopLoading('add-contact-to-property');
    }
  };

  const removeContactFromProperty = async (propertyId: number, contactId: number) => {
    startLoading('remove-contact-from-property', 'Removing contact from property...');
    try {
      await propertiesApi.removeContact(propertyId, contactId);
      await fetchProperties(); // Refresh the list
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove contact from property');
      return false;
    } finally {
      stopLoading('remove-contact-from-property');
    }
  };

  return {
    properties,
    loading,
    error,
    pagination,
    searchParams,
    updateSearchParams,
    createProperty,
    updateProperty,
    deleteProperty,
    addContactToProperty,
    removeContactFromProperty,
    refetch: fetchProperties,
    globalLoading,
    loadingMessage
  };
};

